import { useState, useEffect } from 'react'
import { UploadCloud, FileSpreadsheet, CheckCircle2 } from 'lucide-react'
import api from '../api/client'
import SearchableSelect from '../components/SearchableSelect'
import { errorModal } from '../components/ui'

export default function ImportarFaturas() {
  const [file, setFile] = useState(null)
  const [preview, setPreview] = useState(null)   // {headers, amostra, total, campos_alvo}
  const [mapa, setMapa] = useState({})
  const [parceiroId, setParceiroId] = useState('')
  const [ofertaId, setOfertaId] = useState('')
  const [processarPagas, setProcessarPagas] = useState(true)
  const [parceiros, setParceiros] = useState([])
  const [ofertas, setOfertas] = useState([])
  const [busy, setBusy] = useState(false)
  const [resultado, setResultado] = useState(null)

  useEffect(() => {
    api.get('/parceiros?limit=200').then((r) => setParceiros(r.data.data || [])).catch(() => {})
    api.get('/ofertas?limit=200').then((r) => setOfertas(r.data.data || [])).catch(() => {})
  }, [])

  async function analisar() {
    if (!file) return
    setBusy(true); setResultado(null)
    try {
      const fd = new FormData(); fd.append('arquivo', file)
      const r = await api.post('/faturas/importar/preview', fd)
      setPreview(r.data)
      // auto-mapeia por nome de coluna parecido
      const m = {}
      const lc = (s) => (s || '').toLowerCase()
      for (const alvo of Object.keys(r.data.campos_alvo)) {
        const hit = r.data.headers.find((h) => lc(h).includes(alvo.split('_')[0]) || lc(h).replace(/[^a-z]/g, '').includes(alvo.replace(/_/g, '')))
        if (hit) m[alvo] = hit
      }
      setMapa(m)
    } catch (e) { errorModal(e?.response?.data?.detail || 'Não foi possível ler o arquivo.') }
    finally { setBusy(false) }
  }

  async function importar() {
    setBusy(true); setResultado(null)
    try {
      const fd = new FormData()
      fd.append('arquivo', file)
      fd.append('mapeamento', JSON.stringify(mapa))
      if (parceiroId) fd.append('parceiro_id', parceiroId)
      if (ofertaId) fd.append('oferta_id', ofertaId)
      fd.append('processar_pagas', processarPagas ? 'true' : 'false')
      const r = await api.post('/faturas/importar', fd)
      setResultado(r.data)
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro na importação.') }
    finally { setBusy(false) }
  }

  return (
    <div className="space-y-5 max-w-4xl">
      <div>
        <h1 className="text-2xl font-heading font-bold text-gray-900">Importar Faturas</h1>
        <p className="text-sm text-gray-500 mt-0.5">Importação em massa via arquivo <b>XLSX</b>, <b>CSV</b> ou <b>TXT</b>, com mapeamento de colunas. O cliente é localizado por CPF/CNPJ ou nº de instalação.</p>
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl py-8 cursor-pointer hover:border-accent-300">
          <UploadCloud size={28} className="text-accent-500" />
          <span className="text-sm text-gray-600">{file ? file.name : 'Clique para escolher o arquivo (.xlsx, .csv, .txt)'}</span>
          <input type="file" accept=".xlsx,.csv,.txt" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); setResultado(null) }} />
        </label>
        <div className="flex justify-end mt-3">
          <button disabled={!file || busy} onClick={analisar}
            className="rounded-xl bg-gray-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40">
            {busy && !preview ? 'Lendo...' : 'Analisar arquivo'}
          </button>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <FileSpreadsheet size={18} className="text-accent-500" /> {preview.total} linhas · {preview.headers.length} colunas detectadas
          </div>

          <div>
            <h3 className="font-semibold text-gray-800 mb-2 text-sm">Mapeamento de colunas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(preview.campos_alvo).map(([alvo, label]) => (
                <div key={alvo} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-44 shrink-0">{label}</span>
                  <SearchableSelect value={mapa[alvo] || ''} onChange={(v) => setMapa({ ...mapa, [alvo]: v })}
                    emptyLabel="— ignorar —" className="flex-1"
                    options={preview.headers.map((h) => ({ value: h, label: h }))} />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-100 pt-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Parceiro (aplica a todas)</label>
              <SearchableSelect value={parceiroId} onChange={setParceiroId} emptyLabel="Do cliente / nenhum"
                options={parceiros.map((p) => ({ value: p.id, label: p.nome }))} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Produto/Serviço (aplica a todas)</label>
              <SearchableSelect value={ofertaId} onChange={setOfertaId} emptyLabel="Do cliente / nenhum"
                options={ofertas.map((o) => ({ value: o.id, label: o.nome }))} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={processarPagas} onChange={(e) => setProcessarPagas(e.target.checked)} className="w-4 h-4 accent-accent-500" />
            Processar automaticamente as linhas com status "paga" (gera PV + comissões)
          </label>

          <div className="flex justify-end">
            <button disabled={busy} onClick={importar}
              className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 text-sm font-semibold shadow-md disabled:opacity-40">
              {busy ? 'Importando...' : `Importar ${preview.total} faturas`}
            </button>
          </div>
        </div>
      )}

      {resultado && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold"><CheckCircle2 size={18} /> Importação concluída</div>
          <p className="text-sm text-emerald-900 mt-2">
            {resultado.criadas} faturas criadas · {resultado.processadas} processadas (pagas) · {resultado.erros?.length || 0} erros de {resultado.total_linhas} linhas.
          </p>
          {resultado.erros?.length > 0 && (
            <ul className="mt-2 text-xs text-rose-700 list-disc pl-5 max-h-40 overflow-y-auto">
              {resultado.erros.slice(0, 30).map((e, i) => <li key={i}>Linha {e.linha}: {e.motivo}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
